import * as Sentry from "@sentry/browser";

// We morph instead of replacing the body, so an element
// morphdom PRESERVES never leaves the DOM: Stimulus sees
// no mutation for it and runs no lifecycle, leaving its
// controller looking at markup that was rewritten
// underneath it. This pass supplies the missing
// disconnect/connect, for both page and frame renders.
//
// Note the asymmetry, because it is load-bearing: an
// element morphdom REPLACES does leave the DOM, so
// Stimulus runs its own lifecycle for that one AND this
// pass reconnects it again, two connects against one
// disconnect. Controllers therefore cannot assume
// `connect()` is paired or runs once; per-instance state
// belongs in `initialize()`. See
// `controllers/users/courier_quote_controller.js`, where
// allocating handlers per connect leaked one listener per
// morph (CAL-401).
//
// DO NOT REMOVE THE try/catch BLOCKS BELOW.
//
// This runs inside the `event.detail.render` callback we
// hand to Turbo, and Turbo has no try/catch of its own:
// `Visit.render()` awaits our callback and only then
// calls `visitRendered()` and `visit.complete()`. So a
// single controller throwing in `connect()` rejects that
// promise and the visit never completes: `turbo:load`
// never fires, `hideVisitProgressBar()` never runs, and
// the blue progress bar spins forever over a page that
// looks perfectly rendered. Every controller after the
// throwing one is left disconnected too, because an
// unguarded throw also aborts the loop.
//
// That is not hypothetical: `location_map_controller`
// throws "Map container is already initialized." on every
// morph of the delivery checkout page, which stranded the
// bar for real customers (CAL-401, Sentry CALISTO-79).
//
// Guarding each step separately is deliberate: a
// controller whose `disconnect()` throws still gets its
// `connect()` attempted, so it is not left permanently
// dead for the rest of the session.
const runLifecycleStep = (controller, step) => {
  try {
    controller[step]();
  } catch (error) {
    reportFailure(controller, step, error);
  }
};

// Loud, never silent: a swallowed exception here means a
// controller is quietly not running on the morphed page.
// The identifier is tagged so the next person knows which
// controller broke without reading a stack trace.
const reportFailure = (controller, step, error) => {
  const identifier = controller.identifier || "unknown";

  console.error(
    `Stimulus controller "${identifier}" threw during ` +
      `${step}() after a morph`,
    error
  );

  Sentry.captureException(error, {
    tags: {
      stimulus_controller: identifier,
      morph_reconnect_step: step,
    },
  });
};

// Re-runs the Stimulus lifecycle for every controller that
// is still connected, so controllers pick up the DOM
// morphdom just rewrote underneath them.
//
// Takes the controller list (`Stimulus.controllers`) as an
// argument rather than reading the global, so it can be
// exercised without booting the whole entrypoint.
export default function reconnectControllers(controllers) {
  controllers.forEach((controller) => {
    const connectedContexts =
      controller.context?.module?.connectedContexts;

    if (!connectedContexts?.has(controller.context)) return;

    runLifecycleStep(controller, "disconnect");
    runLifecycleStep(controller, "connect");
  });
}
